import React, { useState } from 'react';
import { requestOtp, verifyOtp } from '../services/api';

const spinnerKeyframes = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;

const Spinner = () => (
  <span style={{
    display: 'inline-block', width: '18px', height: '18px',
    border: '3px solid rgba(255,255,255,0.3)', borderTopColor: '#fff',
    borderRadius: '50%', animation: 'spin 0.8s linear infinite',
    verticalAlign: 'middle', marginRight: '8px',
  }} />
);

const styles = {
  card: {
    background: '#fff',
    borderRadius: '12px',
    padding: '40px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
  },
  heading: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1a202c',
    marginBottom: '8px',
  },
  subheading: {
    fontSize: '14px',
    color: '#718096',
    marginBottom: '32px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#4a5568',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '16px',
    outline: 'none',
    transition: 'border-color 0.2s',
    marginBottom: '20px',
  },
  otpInput: {
    width: '100%',
    padding: '16px',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '28px',
    textAlign: 'center',
    letterSpacing: '12px',
    outline: 'none',
    marginBottom: '20px',
  },
  button: {
    width: '100%',
    padding: '14px',
    background: '#9C3D97',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  error: {
    background: '#fed7d7',
    color: '#c53030',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  success: {
    background: '#c6f6d5',
    color: '#276749',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
  },
};

export default function LoginPage({ onSuccess }) {
  const [step, setStep] = useState('email'); // 'email' | 'otp'
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  async function handleEmailSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await requestOtp(email);
      if (result.error) {
        setError(result.error);
      } else {
        setMessage(result.message);
        setStep('otp');
      }
    } catch {
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleOtpSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await verifyOtp(email, otpCode);
      if (result.error) {
        setError(result.error);
      } else {
        onSuccess();
      }
    } catch {
      setError('Unable to verify. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.card}>
      <style>{spinnerKeyframes}</style>
      <h1 style={styles.heading}>
        {step === 'email' ? 'Sign In' : 'Verify Your Email'}
      </h1>
      <p style={styles.subheading}>
        {step === 'email'
          ? 'Enter your work email address to get started.'
          : `We sent a 6-digit code to ${email}`}
      </p>

      {error && <div style={styles.error}>{error}</div>}
      {message && step === 'otp' && <div style={styles.success}>{message}</div>}

      {step === 'email' ? (
        <form onSubmit={handleEmailSubmit}>
          <label style={styles.label}>Work Email Address</label>
          <input
            style={styles.input}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your.email@example.com"
            required
            autoFocus
          />
          <button
            style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
            type="submit"
            disabled={loading}
          >
            {loading ? <><Spinner />Sending code...</> : 'Send Verification Code'}
          </button>
        </form>
      ) : (
        <form onSubmit={handleOtpSubmit}>
          <label style={styles.label}>Verification Code</label>
          <input
            style={styles.otpInput}
            type="text"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            maxLength={6}
            required
            autoFocus
          />
          <button
            style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
            type="submit"
            disabled={loading || otpCode.length !== 6}
          >
            {loading ? <><Spinner />Verifying...</> : 'Verify & Sign In'}
          </button>
          <button
            type="button"
            onClick={() => { setStep('email'); setError(''); setMessage(''); setOtpCode(''); }}
            style={{
              ...styles.button,
              background: 'transparent',
              color: '#9C3D97',
              marginTop: '12px',
            }}
          >
            Use a different email
          </button>
        </form>
      )}
    </div>
  );
}
